'use client';

import { Card, CardBody } from '@nextui-org/react';
import { Rate } from '@/components/ui/rate';
import { RelativeDate } from '@/components/ui/relative-date';
import { Quote } from 'lucide-react';
import Image from 'next/image';

import { IReview } from '@/entities/review/types';
import { Link } from '@/shared/navigation';
import { ROUTES } from '@/shared/routes/routes';
import { resolvePublicMediaUrl } from '@/shared/utils/resolve-public-media-url';

const DESC_PREVIEW = 200;

const truncate = (text: string, max: number) => {
  const t = text.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max).trim()}…`;
};

const initials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const reviewAuthor = (review: IReview) =>
  review.authorName?.trim() || review.user?.username || 'Покупатель';

export const HomeReviewsStrip = ({ reviews }: { reviews: IReview[] }) => {
  if (!reviews?.length) {
    return null;
  }

  return (
    <section
      className="w-full py-10 md:py-14"
      aria-labelledby="home-reviews-heading"
    >
      <div className="mb-6 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
        <div className="flex items-start gap-3">
          <span className="mt-1 inline-flex rounded-xl bg-gradient-to-br from-warning/20 to-warning/5 p-2.5 text-warning shadow-sm ring-1 ring-warning/10">
            <Quote className="h-6 w-6 shrink-0" aria-hidden />
          </span>
          <div>
            <h2
              id="home-reviews-heading"
              className="text-2xl font-bold tracking-tight text-foreground md:text-3xl"
            >
              Отзывы покупателей
            </h2>
            <p className="mt-1.5 max-w-xl text-sm leading-relaxed text-default-600 md:text-base">
              Реальные отзывы о моих мастер-классах. Полные отзывы смотрите на
              странице каждого мастер-класса.
            </p>
          </div>
        </div>
        <Link
          href={ROUTES.masterclasses}
          className="text-sm font-semibold text-warning-700 underline-offset-4 hover:underline dark:text-warning-400 md:shrink-0"
        >
          В каталог →
        </Link>
      </div>

      <div className="-mx-4 px-4 md:mx-0 md:px-0">
        <div
          className="flex flex-nowrap gap-4 overflow-x-auto pb-2 [-webkit-overflow-scrolling:touch] scroll-smooth overscroll-x-contain snap-x snap-mandatory no-scrollbar"
          role="list"
          aria-label="Отзывы"
        >
          {reviews.map((item) => {
            const author = reviewAuthor(item);
            const photoUrl = resolvePublicMediaUrl(item.images?.[0]);
            const mcHref = item.masterclass?.wordId
              ? `/masterclasses/${item.masterclass.wordId}`
              : ROUTES.masterclasses;

            return (
              <Card
                key={item.id}
                role="listitem"
                className="min-w-[288px] max-w-[320px] shrink-0 snap-start overflow-hidden rounded-2xl border border-default-200/80 bg-gradient-to-b from-content1/90 to-content1/40 shadow-md ring-1 ring-default-200/30 backdrop-blur-sm dark:from-default-100/20 dark:to-default-100/5 dark:ring-default-100/20"
              >
                <CardBody className="flex flex-col gap-4 p-5">
                  <div className="flex gap-3">
                    <span
                      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-warning/25 to-warning/5 text-sm font-semibold text-warning-700 shadow-inner dark:text-warning-400"
                      aria-hidden
                    >
                      {initials(author)}
                    </span>
                    <div className="min-w-0 flex-1 space-y-2">
                      <div>
                        <p className="font-semibold leading-snug text-foreground">
                          {author}
                        </p>
                        <RelativeDate
                          date={item.createdAt}
                          className="mt-0.5 block whitespace-nowrap text-xs text-default-500"
                        />
                      </div>
                      <div className="inline-flex origin-left scale-90">
                        <Rate value={item.rate} readOnly />
                      </div>
                    </div>
                    {photoUrl && (
                      <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-xl">
                        <Image
                          fill
                          sizes="64px"
                          className="object-cover"
                          src={photoUrl}
                          alt={`Фото из отзыва о мастер-классе «${item.masterclass?.name ?? ''}»`}
                        />
                      </div>
                    )}
                  </div>
                  <p className="border-l-2 border-warning/35 pl-3 text-sm leading-relaxed text-default-700 dark:text-default-600">
                    {truncate(item.description, DESC_PREVIEW)}
                  </p>
                  {item.masterclass?.name && (
                    <Link
                      href={mcHref}
                      className="mt-auto text-xs font-medium text-default-500 underline-offset-4 hover:text-warning-700 hover:underline"
                    >
                      МК «{item.masterclass.name}» →
                    </Link>
                  )}
                </CardBody>
              </Card>
            );
          })}
          <div className="shrink-0 w-3 md:w-4" aria-hidden />
        </div>
      </div>
    </section>
  );
};
